/**
 * Excel文件解析工具
 * 支持.xlsx, .xls, .csv格式
 */

import * as XLSX from 'xlsx';

export interface ExcelRow {
  [key: string]: any;
}

export interface ParsedExcelData {
  headers: string[];
  rows: ExcelRow[];
  totalRows: number;
  totalCols: number;
}

export interface FieldTypePrediction {
  name: string;
  type: 'text' | 'number' | 'date' | 'singleSelect';
  suggestedName?: string;
}

/**
 * 检查一行是否为空行
 * 只要行中有任何非空单元格，就不算空行
 */
function isEmptyRow(row: any[]): boolean {
  if (!row || row.length === 0) return true;
  // 只要有一个非空单元格，就不算空行
  return !row.some(cell => {
    const str = String(cell || '').trim();
    return str !== '';
  });
}

/**
 * 检查一行是否像表头行
 * 表头特征：文本类型多、值不重复、非空单元格多
 */
function isHeaderLike(row: any[], sampleRows?: any[][]): boolean {
  if (!row || row.length === 0) return false;
  
  // 计算非空单元格数量
  const nonEmptyCells = row.filter(cell => {
    const str = String(cell || '').trim();
    return str !== '';
  });
  
  if (nonEmptyCells.length === 0) return false;
  
  // 计算文本类型单元格数量
  let textCount = 0;
  const uniqueValues = new Set<string>();
  
  row.forEach(cell => {
    const str = String(cell || '').trim();
    if (str === '') return;
    
    uniqueValues.add(str);
    
    // 检查是否为数字
    const numVal = Number(str);
    if (isNaN(numVal) || str === '') {
      textCount++;
    }
  });
  
  // 表头特征：
  // 1. 文本类型单元格占比高（>50%）
  // 2. 唯一值多（>70%）
  // 3. 非空单元格多（>50%）
  const totalCells = row.length;
  const textRatio = textCount / nonEmptyCells.length;
  const uniqueRatio = uniqueValues.size / nonEmptyCells.length;
  const nonEmptyRatio = nonEmptyCells.length / totalCells;
  
  // 如果与已知表头行相似，也认为是表头（用于检测重复标题行）
  // 注意：这个检测要非常严格，避免误判数据行为标题行
  if (sampleRows && sampleRows.length > 0) {
    const knownHeader = sampleRows[0];
    let exactMatches = 0;
    let totalNonEmpty = 0;
    const minLen = Math.min(row.length, knownHeader.length);
    
    // 统计非空单元格总数（表头行和数据行都算）
    for (let i = 0; i < minLen; i++) {
      const rowVal = String(row[i] || '').trim();
      const headerVal = String(knownHeader[i] || '').trim();
      
      if (headerVal !== '') {
        totalNonEmpty++;
        if (rowVal === headerVal && rowVal !== '') {
          exactMatches++;
        }
      }
    }
    
    // 只有当与表头行完全一致（100%匹配）且匹配的单元格数量足够时，才认为是重复表头
    // 这样可以避免数据行被误判为标题行
    if (totalNonEmpty > 0 && exactMatches === totalNonEmpty && exactMatches >= 3) {
      return true;
    }
  }
  
  // 判断是否为表头行
  // 放宽条件：只要满足以下条件之一即可
  // 1. 文本比例高且唯一值多
  // 2. 非空单元格多且唯一值多
  return (textRatio > 0.5 && uniqueRatio > 0.7) || 
         (nonEmptyRatio > 0.5 && uniqueRatio > 0.7 && textRatio > 0.3);
}

/**
 * 智能识别表头行位置
 * 优先条件：标题栏没有空白列内容，且其列数是最多的
 * 注意：第一个标题行之前的所有行（包括空行）都会被跳过，不做空白行判断
 */
function findHeaderRow(data: any[][]): number {
  // 最多检查前20行
  const maxCheckRows = Math.min(20, data.length);
  
  // 第一步：优先查找"没有空白列内容且列数最多"的行
  // "没有空白列内容"是指：从第一个非空列到最后一个非空列之间，所有列都是非空的
  let bestHeaderIndex = -1;
  let maxNonEmptyCols = 0;
  
  for (let i = 0; i < maxCheckRows; i++) {
    // 跳过空行，继续查找
    if (isEmptyRow(data[i])) continue;
    
    // 找到第一个非空列的索引
    let firstNonEmptyIndex = -1;
    let lastNonEmptyIndex = -1;
    
    for (let j = 0; j < data[i].length; j++) {
      const cell = String(data[i][j] || '').trim();
      if (cell !== '') {
        if (firstNonEmptyIndex === -1) {
          firstNonEmptyIndex = j;
        }
        lastNonEmptyIndex = j;
      }
    }
    
    // 如果找到了非空列
    if (firstNonEmptyIndex >= 0 && lastNonEmptyIndex >= 0) {
      // 检查从第一个非空列到最后一个非空列之间是否有空白列
      let hasEmptyCol = false;
      for (let j = firstNonEmptyIndex; j <= lastNonEmptyIndex; j++) {
        const cell = String(data[i][j] || '').trim();
        if (cell === '') {
          hasEmptyCol = true;
          break;
        }
      }
      
      // 如果这一行没有空白列，且列数最多
      if (!hasEmptyCol) {
        const nonEmptyCols = lastNonEmptyIndex - firstNonEmptyIndex + 1;
        if (nonEmptyCols > maxNonEmptyCols) {
          maxNonEmptyCols = nonEmptyCols;
          bestHeaderIndex = i;
        }
      }
    }
  }
  
  // 如果找到了符合条件的行，返回它
  if (bestHeaderIndex >= 0) {
    return bestHeaderIndex;
  }
  
  // 第二步：如果没有找到符合条件的行，使用原来的逻辑（像表头的行）
  for (let i = 0; i < maxCheckRows; i++) {
    // 跳过空行，继续查找
    if (isEmptyRow(data[i])) continue;
    
    // 检查当前行是否像表头
    if (isHeaderLike(data[i])) {
      // 检查下一行是否不像表头（数据行）
      if (i + 1 < data.length && !isHeaderLike(data[i + 1], [data[i]])) {
        return i;
      }
    }
  }
  
  // 第三步：如果都没找到，返回第一个非空行
  for (let i = 0; i < maxCheckRows; i++) {
    if (!isEmptyRow(data[i])) {
      return i;
    }
  }
  
  return 0;
}

/**
 * 跳过所有空白行
 * 不进行行数判断，直接跳过所有空白行，只导入包含数据的行
 */
function skipBlankRows(rows: any[][]): any[][] {
  const result: any[][] = [];
  
  for (let i = 0; i < rows.length; i++) {
    if (!isEmptyRow(rows[i])) {
      // 只添加非空行，所有空白行都跳过
      result.push(rows[i]);
    }
  }
  
  return result;
}

/**
 * 解析Excel文件
 */
export function parseExcelFile(file: File): Promise<ParsedExcelData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // 读取第一个工作表
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // 转换为JSON格式
        const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: ''
        });
        
        if (jsonData.length === 0) {
          reject(new Error('Excel文件为空'));
          return;
        }
        
        // 限制行数和列数（按需可调）
        const maxRows = 30000;
        const maxCols = 300;
        
        const limitedData = jsonData.slice(0, maxRows).map(row => 
          row.slice(0, maxCols)
        );
        
        // 智能识别表头行位置
        // 第一个标题行之前的所有行（包括空行）都会被跳过，不做空白行判断
        const headerRowIndex = findHeaderRow(limitedData);
        const headers = (limitedData[headerRowIndex] || []).map((h: any, index: number) => {
          const headerStr = String(h || '').trim();
          return headerStr || `列${index + 1}`;
        });
        
        // 获取表头行之后的数据
        // 表头行之前的所有行（包括空行）都已经被跳过
        let dataRows = limitedData.slice(headerRowIndex + 1);
        
        // 智能跳过重复的标题行
        // 注意：只在数据行中检测，并且要非常严格，避免误判数据行
        const filteredDataRows: any[][] = [];
        const knownHeader = limitedData[headerRowIndex];
        
        for (let i = 0; i < dataRows.length; i++) {
          // 跳过空行（会在后续处理）
          if (isEmptyRow(dataRows[i])) {
            continue;
          }
          
          // 检查是否是重复的标题行
          // 后续的标题行检测需要和第一个标题行100%匹配才能判定为标题行
          let exactMatches = 0;
          let totalNonEmpty = 0;
          const minLen = Math.min(dataRows[i].length, knownHeader.length);
          
          for (let j = 0; j < minLen; j++) {
            const rowVal = String(dataRows[i][j] || '').trim();
            const headerVal = String(knownHeader[j] || '').trim();
            
            if (headerVal !== '') {
              totalNonEmpty++;
              if (rowVal === headerVal && rowVal !== '') {
                exactMatches++;
              }
            }
          }
          
          // 判断是否为重复标题行：匹配度必须 100%
          const isDuplicateHeader = totalNonEmpty > 0 && exactMatches === totalNonEmpty && exactMatches >= 3;
          
          if (isDuplicateHeader) {
            // 跳过重复标题行
            continue;
          }
          
          filteredDataRows.push(dataRows[i]);
        }
        
        // 跳过所有空白行
        const cleanedDataRows = skipBlankRows(filteredDataRows);
        
        // 转换为对象数组
        const rows: ExcelRow[] = cleanedDataRows.map((row: any[]) => {
          const rowObj: ExcelRow = {};
          headers.forEach((header, index) => {
            rowObj[header] = row[index] !== undefined ? row[index] : '';
          });
          return rowObj;
        });
        
        resolve({
          headers,
          rows,
          totalRows: rows.length,
          totalCols: headers.length
        });
      } catch (error: any) {
        reject(new Error(`解析Excel文件失败: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('读取文件失败'));
    };
    
    reader.readAsBinaryString(file);
  });
}

/**
 * 智能识别字段类型
 */
export function predictFieldTypes(
  headers: string[],
  sampleRows: ExcelRow[]
): FieldTypePrediction[] {
  // 判断字符串是否为纯数字（可含符号和小数点）
  const isPureNumberString = (val: string) => {
    return /^-?\d+(\.\d+)?$/.test(val.trim());
  };
  
  const predictions: FieldTypePrediction[] = [];
  
  headers.forEach((header, index) => {
    const sampleValues = sampleRows
      .map(row => row[header])
      .filter(val => val !== null && val !== undefined && val !== '');
    
    // 如果样本中存在非纯数字字符串，则直接降级为文本，避免导入时因混合字符报数值错误
    const hasMixedNumber = sampleValues.some(val => {
      const str = String(val).trim();
      if (str === '') return false;
      // 仅当包含非数字字符且不能被安全识别为纯数字时，视为混合
      const canParseNumber = !isNaN(Number(str));
      return !isPureNumberString(str) && !canParseNumber;
    });
    
    if (sampleValues.length === 0) {
      predictions.push({
        name: header,
        type: 'text',
        suggestedName: header
      });
      return;
    }
    
    // 分析数据类型
    let isNumber = true;
    let isDate = false;
    let uniqueValues = new Set<string>();
    
    sampleValues.forEach(val => {
      const strVal = String(val).trim();
      uniqueValues.add(strVal);
      
      // 检查是否为数字
      if (isNumber) {
        const numVal = Number(strVal);
        if (isNaN(numVal)) {
          isNumber = false;
        }
      }
      
      // 检查是否为日期
      if (!isDate) {
        const dateVal = new Date(strVal);
        if (!isNaN(dateVal.getTime()) && strVal.match(/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/)) {
          isDate = true;
        }
      }
    });
    
    // 判断字段类型
    let fieldType: 'text' | 'number' | 'date' | 'singleSelect' = 'text';
    
    if (hasMixedNumber) {
      fieldType = 'text';
    } else if (isDate) {
      fieldType = 'date';
    } else if (isNumber) {
      fieldType = 'number';
    } else if (uniqueValues.size <= 10 && sampleValues.length > 0) {
      // 如果唯一值少于10个，可能是单选字段
      fieldType = 'singleSelect';
    } else {
      fieldType = 'text';
    }
    
    // 智能命名
    let suggestedName = header;
    if (!header || header.startsWith('列')) {
      // 根据数据内容预测字段名
      const firstValue = sampleValues[0];
      const strVal = String(firstValue).toLowerCase();
      
      if (strVal.includes('姓名') || strVal.includes('名字') || strVal.includes('name')) {
        suggestedName = '姓名';
      } else if (strVal.includes('年龄') || strVal.includes('age')) {
        suggestedName = '年龄';
      } else if (strVal.includes('电话') || strVal.includes('手机') || strVal.includes('phone')) {
        suggestedName = '电话';
      } else if (strVal.includes('邮箱') || strVal.includes('email')) {
        suggestedName = '邮箱';
      } else if (strVal.includes('地址') || strVal.includes('address')) {
        suggestedName = '地址';
      } else if (strVal.includes('日期') || strVal.includes('date') || strVal.includes('时间')) {
        suggestedName = '日期';
      } else {
        suggestedName = `字段${index + 1}`;
      }
    }
    
    predictions.push({
      name: header,
      type: fieldType,
      suggestedName
    });
  });
  
  return predictions;
}

/**
 * 检查第一行是否为字段名
 */
export function isFirstRowHeader(sampleRows: ExcelRow[]): boolean {
  if (sampleRows.length === 0) return true;
  
  const firstRow = sampleRows[0];
  const secondRow = sampleRows[1];
  
  if (!secondRow) return true;
  
  // 检查第一行是否更像表头（文本类型多，值不重复）
  let firstRowTextCount = 0;
  let secondRowTextCount = 0;
  let firstRowUniqueCount = 0;
  let secondRowUniqueCount = 0;
  
  Object.values(firstRow).forEach(val => {
    const strVal = String(val).trim();
    if (isNaN(Number(strVal)) && strVal.length > 0) {
      firstRowTextCount++;
    }
    if (strVal.length > 0) {
      firstRowUniqueCount++;
    }
  });
  
  Object.values(secondRow).forEach(val => {
    const strVal = String(val).trim();
    if (isNaN(Number(strVal)) && strVal.length > 0) {
      secondRowTextCount++;
    }
    if (strVal.length > 0) {
      secondRowUniqueCount++;
    }
  });
  
  // 如果第一行文本比例高且唯一值多，可能是表头
  return firstRowTextCount > secondRowTextCount || 
         (firstRowUniqueCount === Object.keys(firstRow).length && firstRowUniqueCount > secondRowUniqueCount);
}

